import { useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Box, Collapse, Stack, Typography, useTheme, Zoom, Tooltip } from "@mui/material";
import Icon from "../../../../Icon/Icon";
import { ChipInfo } from "../../../../MainComponents/ChipInfo/ChipInfo";
import { useConstants } from "../Main/useConstants";
import { InvestigationItemContainer } from "../components/InvestigationItemContainer";
import { InvestigationButton } from "../components/InvestigationButton";
import { SearchInvolved } from "./Modals/SearchInvolved";
import { CollaboratorDetail } from "./CollaboratorDetail";
import { CareerDetail } from "./CareerDetail";


// Sección colapsable (showhide) con título y descripción
const CollapsibleSection = ({ title, description, color, children, defaultOpen = false, onToggle }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const accentColor = color || theme.palette.primary.main;

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (onToggle) onToggle(next);
      return next;
    });
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={handleToggle}
        sx={{ cursor: "pointer" }}
      >
        <Box>
          <Typography fontSize={14} fontWeight={600} color={accentColor}>
            {title}
          </Typography>
          {description && (
            <Typography fontSize={12} color="secondary">
              {description}
            </Typography>
          )}
        </Box>
        <Icon
          name="arrow_down_2"
          size={20}
          color={accentColor}
          style={{
            transform: open ? "rotate(-180deg)" : "rotate(0deg)",
            transition: "transform 0.5s",
            flexShrink: 0,
          }}
        />
      </Box>
      <Collapse in={open}>
        <Box sx={{ paddingTop: "12px" }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};


// Involucrado para la vista en la lista
export const InvolvedView = ({
  index,
  kind,
  color,
  involved,
  allInvolveds,
  setSelectedInvolved,
  setShowDrawer,
  handleSaveInvolveds,
  justView,
  hide,
}) => {

  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const { enums, functions } = useConstants();

  const [showSearchModal, setShowSearchModal] = useState(false);
  // Activa la consulta en tiempo real de los datos de carrera al abrir el toggle
  const [careerActive, setCareerActive] = useState(false);

  const handleViewInfo = () => {
    setSelectedInvolved({...involved, index});
    setShowDrawer(true);
  };

  const handleOpenSearch = () => {
    setShowSearchModal(true);
  };

  const handleSelectFromSearch = async (result, searchType) => {
    if (!handleSaveInvolveds || !allInvolveds || index === undefined) {
      console.log("Cannot update: missing props", { handleSaveInvolveds, allInvolveds, index });
      return;
    }

    // Build the name from the search result (source of truth)
    const searchResultName = result.name ||
      `${result.firstName || result.Nombre || ""} ${result.lastName || result.Apellido || ""}`.trim();

    // Map the search result to the involved data structure
    // The endpoint is the source of truth, so we overwrite with search result values
    const updatedInvolved = {
      ...involved,
      // Name is always overwritten from search result (source of truth)
      name: searchResultName,
      firstName: result.firstName || result.Nombre || "",
      lastName: result.lastName || result.Apellido || "",

      // Basic identifiers
      userId: result.userId || "",
      username: result.username || "",
      additional: result.employee_number || result.employeeNumber || result.userId || result.Identificador || "",

      // Contact
      email: result.email || result.Email || "",

      // Organization
      division: result.division || "",
      department: result.department || "",
      businessUnit: result.businessUnit || "",
      area: result.area || result.division || result.Area || "",
      vertical: result.vertical || result.division || "",

      // Location
      country: result.country || result.Pais || "",
      province: result.province || "",
      location: result.location || "",
      locationName: result.locationName || "",

      // Job info
      relationship: result.position || result.cargo || result.Puesto || "",
      position: result.position || result.cargo || "",
      jobCode: result.jobCode || "",
      costCenter: result.costCenter || "",
      company: result.company || "",

      // Dates
      hireDate: result.hireDate || result.meli_entry_date || "",
      meli_entry_date: result.meli_entry_date || result.hireDate || "",
      positionEntryDate: result.positionEntryDate || "",

      // IDs and tax info
      rfcTaxId: result.rfc_tax_id || result.nationalId || result.Identificador || result.rfcTaxId || "",
      primaryNationalId: result.primaryNationalId || null,
      nationalIds: result.nationalIds || [],

      // Manager and relationships
      manager: result.manager || null,
      relationships: result.relationships || null,
      pbp: result.pbp || null,
      pbpLeader: result.pbpLeader || null,
      pbpLeaderInfo: result.pbpLeaderInfo || null,
      taOwner: result.taOwner || null,

      // Flags
      is_manager: result.is_manager || false,
      employeeType: result.employeeType || searchType,

      // Extended info
      empInfo: result.empInfo || null,
      jobInfo: result.jobInfo || null,
      jobInfoNav: result.jobInfoNav || [],
      empJobRelationships: result.empJobRelationships || [],

      // External employee fields
      businessName: result.businessName || result.companyName || result.RazonSocial || "",
      employeeStatus: result.status || result.Estado || "",
      employeeId: result.ID || result.id || "",
      companyId: result.companyId || result.IdentificadorEmpresa || "",
      gender: result.gender || result.Genero || "",

      // Store the search type used to find this person
      searchType: searchType,
      // Store the raw endpoint response for reference
      sourceData: result,
      _originalResponse: result._originalResponse || null,
      // Mark as imported from search - this makes it read-only
      importedFromSearch: true,

      // Keep existing fields that shouldn't be overwritten
      kind: involved.kind,
      rol: involved.rol,
      indicated: involved.indicated,
      confirm: involved.confirm,
      confirm_text: involved.confirm_text,
      validated: involved.validated,
      details: involved.details,
      reinsidence: involved.reinsidence,
      isManager: involved.isManager,
      companyRelationship: involved.companyRelationship,
      meliEntryDate: involved.meliEntryDate,
    };

    // Update the involveds array
    const newInvolveds = _.cloneDeep(allInvolveds);
    newInvolveds[kind][index] = updatedInvolved;

    try {
      await handleSaveInvolveds(newInvolveds);
      // Modal closing is handled by SearchInvolved after this promise resolves
    } catch (error) {
      console.error("Error updating involved:", error);
      throw error; // Re-throw so SearchInvolved can handle the error state
    }
  };

  if (hide) return <></>;

  return (
    <InvestigationItemContainer 
      color={color}
      sx={{ padding: 2 }} 
    >
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <Typography fontSize={16} fontWeight={600} color={color} >
            {involved.name}
          </Typography>
          <Tooltip
            title={
              involved.confirm_text === "confirmed" ? 
              t("involved.confirmed") :
              involved.confirm_text === "cant_confirm" ? 
              t("involved.not_confirmed") :
              involved.confirm_text === "out" ? 
              t("involved.not_participated") :
              involved.confirm_text === true ? 
              t("involved.confirmed") :
              t("involved.pending_confirmation")
            }
            placement="bottom"
            arrow
            TransitionComponent={Zoom}
            TransitionProps={{ timeout: 150 }}
          >
            <Box ml={.5}>
              <Icon 
                name={
                  involved.confirm_text === "confirmed" ? 
                  "bold_tick_circle" :
                  involved.confirm_text === "cant_confirm" ? 
                  "bold_danger" :
                  involved.confirm_text === "out" ? 
                  "bold_close_circle" :
                  involved.confirm_text === true ? 
                  "bold_tick_circle" :
                  "bold_info_circle"
                }
                color={
                  involved.confirm_text === "confirmed" ? 
                  theme.palette.green.main :
                  involved.confirm_text === "cant_confirm" ? 
                  theme.palette.orange.main :
                  involved.confirm_text === "out" ? 
                  theme.palette.red.main :
                  involved.confirm_text === true ? 
                  theme.palette.green.main :
                  theme.palette.yellow.main
                }
                size={16} 
              />
            </Box>
          </Tooltip>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ChipInfo
            label={
              <span style={{ color: theme.palette.blueGray.main }}>
                {t("involved.indicated_by")}: &nbsp;
                  <b style={{ color: theme.palette.oxfordBlue.main }}>
                    {
                      (involved.indicated?.from === involved.added) ?
                      functions.getUserLabel({ from: enums.typeUser.COMPLAINANT }) :
                      functions.getUserLabel(involved?.indicated) ?? "Otro"
                    }
                  </b>
              </span>
            }
            icon={<Icon name="bold_user_receive" size={24} />}
            color="secondary"
            maxWidth={200}
          />
          {/* Validated badge when imported from search */}
          {
            involved.importedFromSearch &&
            <Tooltip
              title={t("involved.validated_from_search")}
              placement="bottom"
              arrow
              TransitionComponent={Zoom}
              TransitionProps={{ timeout: 150 }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  backgroundColor: `${theme.palette.green.main}15`,
                  borderRadius: "8px",
                  padding: "6px 10px",
                }}
              >
                <Icon name="bold_shield_tick" color={theme.palette.green.main} size={20} />
              </Box>
            </Tooltip>
          }
          {
            !justView &&
            <>
              {/* Only show search button if not already imported */}
              {
                !involved.importedFromSearch &&
                <InvestigationButton
                  icon="outline_search"
                  sx={{ marginRight: 1 }}
                  variant="secondary"
                  onClick={handleOpenSearch}
                />
              }
              {/* Detalle del colaborador en modal (incluye asignación de PBP, etc.).
                  Disponible siempre, incluso para importados desde el buscador,
                  además del detalle inline. */}
              <InvestigationButton
                icon="outline_clipboard_eye"
                sx={{ marginRight: 1 }}
                variant="primary"
                onClick={handleViewInfo}
              />
            </>
          }
        </Stack>
      </Box>

      {/* Detalles colapsables para involucrados importados desde el buscador */}
      {
        involved.importedFromSearch &&
        <Box mt={2}>
          <CollapsibleSection
            title={t("involved.collaborator_detail_title")}
            description={t("involved.collaborator_detail_description")}
            color={color}
          >
            <CollaboratorDetail involved={involved} />
          </CollapsibleSection>

          <hr style={{ border: "none", borderTop: `1px solid ${theme.palette.blueGray200.main}`, margin: "16px 0" }} />

          <CollapsibleSection
            title={t("involved.career_detail_title")}
            description={t("involved.career_detail_description")}
            color={color}
            onToggle={setCareerActive}
          >
            <CareerDetail involved={involved} active={careerActive} />
          </CollapsibleSection>
        </Box>
      }

      {/* Search Modal */}
      <SearchInvolved
        open={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onSelect={handleSelectFromSearch}
        initialData={involved}
      />
    </InvestigationItemContainer>
  )
};